import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Card, CardBody, Col, FormGroup, Input, Label, Row } from "reactstrap";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { useTranslation } from "react-i18next";
import { PostItem } from "hc-entities";
import { publishPost } from "../../store/actions/contentActions";
import { resetModal } from "../../store/actions/modalActions";
import { ChipSelect } from "../../globals/core/ChipSelect/SelectChip";
import { searchTagsAction } from "./Content";
import { UsersService } from "../../globals/services/users.service";
import { showLoader } from "../../store/actions/mainActions";

const animatedComponents = makeAnimated()

export const QuickPostForm = (props) => {
    const { repost, action } = props;
    const [users, setUsers] = useState([]);
    const { currentContent, communities } = useSelector((state) => state.content);
    const { me } = useSelector((state => state.auth));
    const dispatch = useDispatch();
    const { t } = useTranslation('common');

    const checkboxClass = t("direction") === 'rtl' ? 'form-check-right' : 'form-check';

    const postSettings = {
        showInSearch: false,
        onlyToCommunity: false,
        pinToTop: false,
        alertUsers: false
    }

    useEffect(() => {
        const getData = async () => {
            const users = await UsersService().getAll();
            dispatch(showLoader(true))
            setUsers(users)
            dispatch(showLoader(false))
        }
        getData();
    }, [setUsers, dispatch])

    const setCommunities = () => {
        return repost?.communityId ? [communities.find(c => c.value === repost.communityId)] : [];
    }

    const initialPost = {
        communities: setCommunities(),
        content: currentContent,
        tags: repost ? repost.tags : currentContent?.tags,
        post: repost ? repost.post : '',
        owner: repost ? repost.owner : me?._id,
        status: repost ? repost.status : 'draft',
        postSettings: repost ? repost.postSettings : postSettings
    }
    const [post, setPost] = useState(initialPost);

    const handleForm = (prop) => {
        setPost({ ...post, ...prop })
    }

    const quickPost = async (toPublish) => {
        const { afterPostAction } = props;
        if (afterPostAction) {
            const _post = new PostItem({
                ...post,
                contentId: currentContent?.content_id,
                status: toPublish ? 'publish' : post.status
            });
            delete _post['content'];
            switch (action) {
                case 'create':
                    if (currentContent) {
                        await dispatch(publishPost(_post , false , false));
                    }
                    else {
                        await dispatch(publishPost(_post, true , false));
                    }
                    await dispatch(resetModal())
                    await afterPostAction();
                    break;
                case 'edit':
                    if (repost._id) {
                        _post._id = repost._id;
                    }
                    if(currentContent?.type === "quick_post"){
                        await dispatch(publishPost(_post , true , true));
                    }else{
                        await dispatch(publishPost(_post , false , true));
                    }
                    await dispatch(resetModal())
                    await afterPostAction();
                    break
                default:
                    break;
            }
        }
    }

    const handleMultiSelect = prop => {
        setPost({ ...post, ...prop })
    }

    const renderForm = () => {
        const _communities = communities.filter(c => c.label.indexOf('- Community') != -1)
        return (
            <Card>
                <CardBody>
                    <div className="row">
                        <div className="col-12 px-2">
                            <h4>Context</h4>
                            <Row className="mt-3">
                                <Col md={6} className="px-2">
                                    <FormGroup className="mb-3 mt-3 mt-lg-0 templating-select select2-container">
                                        <h5 className="mt-4 my-2"><Label htmlFor="communities">Pick a community to post to (1 or more)</Label></h5>
                                        <Select
                                            id="communities"
                                            value={post.communities}
                                            isMulti={true}
                                            onChange={(selected) => handleMultiSelect({ communities: selected })}
                                            options={_communities}
                                            classNamePrefix="select2-selection"
                                            closeMenuOnSelect={false}
                                            components={animatedComponents}
                                        />
                                    </FormGroup>
                                    <FormGroup className="mb-3 mt-3 mt-lg-0 templating-select select2-container">
                                        <h5 className="mt-4" ><Label htmlFor="tags">Tags</Label></h5>
                                        <ChipSelect
                                            placeholder="Search for tags"
                                            tags={post.tags ? post.tags : []}
                                            setTags={(selected) => handleMultiSelect({ tags: selected })}
                                            searchAction={searchTagsAction}
                                        />
                                    </FormGroup>
                                    <FormGroup className="mb-3 mt-3 mt-lg-0">
                                        <h5 className="mt-4" ><Label htmlFor="post">Your Post</Label></h5>
                                        <textarea onChange={e => { handleForm({ post: e.target.value }) }} value={post.post || ""} rows="6" className="form-control" id="post" placeholder="" />
                                    </FormGroup>
                                </Col>
                                <Col md={6} className="px-2">
                                    <div className="mb-3 mt-3 mt-lg-0">
                                        <h5 className="mt-4" >Publishing Settings</h5>
                                        <FormGroup>
                                            <div className={`${checkboxClass} form-check-outline form-check-primary`}>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="showInSearch"
                                                    checked={post.postSettings.showInSearch}
                                                    onChange={(e) => handleForm({ postSettings: { ...post.postSettings, showInSearch: e.target.checked } })}
                                                />
                                                <label
                                                    className="form-check-label"
                                                    htmlFor="showInSearch"
                                                >
                                                    Show item in Search Results
                                                </label>
                                            </div>
                                        </FormGroup>
                                        <FormGroup>
                                            <div className={`${checkboxClass} form-check-outline form-check-primary`}>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="onlyToCommunity"
                                                    checked={post.postSettings.onlyToCommunity}
                                                    onChange={(e) => handleForm({ postSettings: { ...post.postSettings, onlyToCommunity: e.target.checked } })}
                                                />
                                                <label
                                                    className="form-check-label"
                                                    htmlFor="onlyToCommunity"
                                                >
                                                    Item available only to community
                                                </label>
                                            </div>
                                        </FormGroup>
                                        <FormGroup>
                                            <div className={`${checkboxClass} form-check-outline form-check-primary`}>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="alertUsers"
                                                    checked={post.postSettings.alertUsers}
                                                    onChange={(e) => handleForm({ postSettings: { ...post.postSettings, alertUsers: e.target.checked } })}
                                                />
                                                <label
                                                    className="form-check-label"
                                                    htmlFor="alertUsers"
                                                >
                                                    Alert users about this new item
                                                </label>
                                            </div>
                                        </FormGroup>
                                        <FormGroup>
                                            <div className={`${checkboxClass} form-check-outline form-check-primary`}>
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id="pinToTop"
                                                    checked={post.postSettings.pinToTop}
                                                    onChange={(e) => handleForm({ postSettings: { ...post.postSettings, pinToTop: e.target.checked } })}
                                                />
                                                <label
                                                    className="form-check-label"
                                                    htmlFor="pinToTop"
                                                >
                                                    Pin to Top of Community Feed
                                                </label>
                                            </div>
                                        </FormGroup>
                                    </div>
                                    <FormGroup className="mb-3 mt-3 mt-lg-0">
                                        <h5 className="mt-4" ><Label htmlFor="owner">Owner</Label></h5>
                                        <Input
                                            disabled={action === 'edit' ? true : false}
                                            value={post.owner}
                                            onChange={(e) => handleForm({ owner: e.target.value })}
                                            type="select"
                                            className="form-select"
                                            id="owner">
                                            {users.map(u => <option value={u._id} >{u?.firstName + ' ' + u?.lastName}</option>)}
                                        </Input>
                                    </FormGroup>
                                    <FormGroup className="mb-3 mt-3 mt-lg-0">
                                        <h5 className="mt-4" > <Label htmlFor="_status">Status</Label></h5>
                                        <Input
                                            value={post.status}
                                            onChange={(e) => handleForm({ status: e.target.value })}
                                            type="select"
                                            className="form-select"
                                            id="_status">
                                            <option defaultValue value="draft">Draft</option>
                                            <option value="publish">Publish</option>
                                        </Input>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </CardBody>
            </Card>
        )
    }

    const renderFormTitle = () => {
        if (currentContent) {
            return `${currentContent?.title} | Uploaded by ${currentContent?.userName}`;
        } else {
            return `Quick Post`;
        }
    }

    return (
        <div className="container">
            <div className="row">
                <h3>
                    {currentContent ? 'Publish Content' : 'Publish'}
                </h3>
            </div>
            <div className="row my-2">
                <div className="h4">
                    {renderFormTitle()}
                </div>
            </div>
            <div className="row my-3">
                {renderForm()}
            </div>
            <div className="row mt-5 mb-3 justify-content-end">
                <div className="col-6 d-flex justify-content-end">
                    <Button onClick={() => quickPost(false)} color="primary" className="w-md mx-4">{'Save & Exit'}</Button>
                    {action !== 'edit' ? <Button onClick={() => quickPost(true)} color="success" className="w-md">{'Publish'}</Button> : null}
                </div>
            </div>
        </div>
    )


}